import cn from "classnames";
import { FC, MouseEventHandler, PropsWithChildren, useRef } from "react";
import { createPortal } from "react-dom";
import { useContainer } from "../hooks/useContainer";

export type ModalProps = {
	show: boolean;
	showBackground?: boolean;
	onClickOutside: MouseEventHandler;
	className?: string;
};

export const Modal: FC<PropsWithChildren<ModalProps>> = ({ show, showBackground = true, onClickOutside, className = "w-full max-w-3xl", ...props }) => {
	var portalId = useContainer(show);

	// Only treat a backdrop click as "click outside" when the press *started* on
	// the backdrop. A click event fires on the common ancestor of the mousedown
	// and mouseup targets, so selecting text inside the dialog and releasing the
	// mouse on the backdrop would otherwise fire onClick here and dismiss it
	// before the user can copy. Tracking the mousedown origin matches the
	// industry-norm behavior (close on press-outside, not release-outside).
	const pressStartedOnBackdrop = useRef(false);

	if (!portalId) {
		return <></>;
	}

	var el = document.getElementById(portalId);

	if (!el) {
		return <></>;
	}

	return createPortal(
		<>
			{show && (
				<div
					className={cn("w-screen h-screen fixed top-0 left-0 z-50 flex overflow-y-auto",
						{ "background bg-dialog-behind backdrop-filter backdrop-blur-sm": showBackground })}
					onMouseDown={(e) => { pressStartedOnBackdrop.current = e.target === e.currentTarget; }}
					onClick={(e) => {
						if (pressStartedOnBackdrop.current) {
							onClickOutside(e);
						}
					}}>
					<div
						className={cn("rounded bg-dialog-background m-auto self-center ", className)}
						onClick={(e) => e.stopPropagation()}>
						{props.children}
					</div>
				</div>
			)}
		</>,
		el
	);
};
